import WelcomeLibraryThumbnail from '@assets/welcome-library/optimized/welcome-library-video/welcome-library-video_poster.jpg';
import { AuthenticatedImage } from '@components/AuthenticatedImage/AuthenticatedImage';
import { LibraryCardPlaceholder } from '@components/LibraryThumbnail/LibraryCardPlaceholder';
import { useLibraryCover } from '@components/LibraryThumbnail/useLibraryThumbnail';
import { ownedByLabel } from '@lib/utils/libraryOwnership';
import { formatLibraryStats, joinLibraryStats } from '@lib/utils/libraryStats';
import { getLibraryUrl } from '@lib/utils/url';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { LibraryStore } from '@stores/Library/Library';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import css from './LibraryListRow.module.scss';
import { LibraryNameBadges } from './LibraryNameBadges';

interface LibraryListRowProps {
  library: LibraryStore;
  /**
   * Takes the library's id and name rather than being pre-curried with them, so the list can
   * hand down the page's own stable callback instead of building one closure per row — a fresh
   * function per row would defeat this component's `observer` memo and re-render every row
   * whenever any one of them changes.
   */
  onMobileMenuClick?: (libraryId: string, libraryName: string, thumbnailBlobUrl?: string) => void;
  /** Hidden while its preview card floats above the mobile context drawer. */
  isHidden?: boolean;
}

/**
 * Mobile-only library row. Desktop's list view is `LibrariesTable` (a data grid),
 * so this deliberately carries no metadata columns, no pin, and no date:
 *
 * - **No date.** `title` can't disambiguate added-vs-updated on a touchscreen, and
 *   the default sort already puts the most recently updated library on top.
 * - **No pin.** Its reveal is hover-only, so it never appears on touch — which is
 *   also true of the card grid today.
 */
export const LibraryListRow = observer(function LibraryListRow({
  library,
  onMobileMenuClick,
  isHidden = false,
}: LibraryListRowProps) {
  // Doubles as the element the lazy cover below is observed on and as the handle the ⋯
  // button reads the rendered thumbnail from.
  const rowRef = useRef<HTMLAnchorElement>(null);

  const id = library.libraryId;
  const name = library.name || '';
  // Honour the user's chosen cover, as the gallery and Workspace do. Deliberately
  // the raw `userMetadata` value, not `library.thumbnailMediaId` — that getter falls
  // back to `latestMediaIds[0]`, which the hook would read as an explicit choice and
  // stop walking its fallback chain.
  const isDemo = !!library.isDemo;
  // The demo library renders its bundled poster below, so a fetched cover would be
  // discarded — that reason is OR'd with the hook's own out-of-window one.
  const { url, token, onError } = useLibraryCover(library, rowRef, {
    thumbnailMediaId: library.data?.userMetadata?.thumbnailMediaId,
    disabled: isDemo,
  });
  const stats = formatLibraryStats(library);
  // Last in the meta line, and only for libraries somebody else owns. Last because the line
  // ellipsises on a narrow screen and the stats are what the row is for — the owner qualifies
  // the library rather than describing it, and the full text stays reachable through the ⋯
  // drawer's preview card.
  const ownerName = library.otherOwnerName;

  const handleMobileMenuClick = (e: React.MouseEvent) => {
    // Inside the row's <Link>, so cancel navigation as well as bubbling — the same
    // contract LibraryCardView uses for its overlay buttons.
    e.preventDefault();
    e.stopPropagation();
    // Read the already-rendered thumbnail so the drawer's preview card doesn't
    // flicker while it refetches.
    const img = rowRef.current?.querySelector('img');
    onMobileMenuClick?.(id, name, img?.src || undefined);
  };

  return (
    <Link
      ref={rowRef}
      to={getLibraryUrl(id, name)}
      className={classNames(css.row, { [css.hidden]: isHidden })}
    >
      <div className={css.thumbnailWrapper}>
        {/* The demo library gets its bundled cover, matching the gallery card. */}
        {isDemo ? (
          <img className={css.thumbnail} src={WelcomeLibraryThumbnail} alt={name} />
        ) : url ? (
          <AuthenticatedImage
            className={css.thumbnail}
            src={url}
            token={token}
            alt={name}
            onError={onError}
          />
        ) : (
          <div className={css.thumbnailPlaceholder}>
            <LibraryCardPlaceholder seed={id} fit="contain" />
          </div>
        )}
      </div>

      <div className={css.titleWrapper}>
        <LibraryNameBadges library={library} />
        <div className={css.metaLine}>
          {joinLibraryStats([
            stats.count,
            stats.duration,
            stats.size,
            ownerName ? ownedByLabel(ownerName) : null,
          ])}
        </div>
      </div>

      {/* No verb rail here, unlike the desktop table and the card grid. The row is
          73px tall on a 360px screen and already spends its width on a thumbnail, a
          two-line name and the ⋯ — three more hit targets left nothing for the name.
          The verbs live in the ⋯ context drawer instead, which is why
          `MobileLibraryContextDrawer` keeps them while the desktop kebab drops them. */}
      {!library.isReadOnly && (
        <button
          className={css.mobileMenuButton}
          onClick={handleMobileMenuClick}
          aria-label={`Manage ${name}`}
        >
          <MoreVertIcon fontSize="small" />
        </button>
      )}
    </Link>
  );
});
